// src/pages/Banners/Banners.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Alert, Row, Col, Form, Badge, Pagination, InputGroup, Spinner, Image } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaCalendarAlt, FaToggleOn, FaToggleOff, FaImage } from 'react-icons/fa';
import { bannersAPI, createFormData } from '../../services/api';
import FormModal from '../../components/Common/FormModal';
import ConfirmModal from '../../components/Common/ConfirmModal';

const Banners = () => {
    const [banners, setBanners] = useState([]);
    const [filteredBanners, setFilteredBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedBanner, setSelectedBanner] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [imagePreview, setImagePreview] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [currentImagePath, setCurrentImagePath] = useState(null);

    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 1
    });

    // Statistics state
    const [bannerStats, setBannerStats] = useState({
        total: 0,
        active: 0,
        inactive: 0
    });

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        link_url: '',
        position: '',
        is_active: '1',
        starts_at: '',
        ends_at: ''
    });

    // Base URL for banner images
    const BASE_URL = import.meta.env.VITE_API_URL;

    useEffect(() => {
        fetchBanners();
        calculateLocalStats();
    }, [pagination.page, statusFilter]);

    useEffect(() => {
        const handleSearch = () => {
            if (searchTerm.trim()) {
                const filtered = banners.filter(banner => {
                    const searchLower = searchTerm.toLowerCase();
                    return (
                        banner.title?.toLowerCase().includes(searchLower) ||
                        banner.description?.toLowerCase().includes(searchLower)
                    );
                });
                setFilteredBanners(filtered);
            } else {
                setFilteredBanners(banners);
            }
        };

        // Debounce search
        const timeoutId = setTimeout(() => {
            handleSearch();
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, banners]);

    const fetchBanners = async () => {
        try {
            setLoading(true);
            setError('');

            console.log('Fetching banners...');
            const response = await bannersAPI.list();

            console.log('Banners API Response:', response);

            let bannersData = [];

            if (response.data) {
                // Handle different response structures
                if (response.data.data && Array.isArray(response.data.data)) {
                    // Response structure: { data: [...] }
                    bannersData = response.data.data;
                } else if (Array.isArray(response.data)) {
                    // Response structure: [...]
                    bannersData = response.data;
                }
            }

            if (!Array.isArray(bannersData)) {
                console.error('Invalid banners data format:', response.data);
                bannersData = [];
            }

            console.log('Processed banners data:', bannersData);

            setBanners(bannersData);
            setFilteredBanners(bannersData);
            setPagination(prev => ({
                ...prev,
                total: bannersData.length,
                pages: Math.ceil(bannersData.length / prev.limit)
            }));

            calculateLocalStats(bannersData);

        } catch (err) {
            console.error('Error fetching banners:', err);
            setError(err.response?.data?.message || err.message || 'Failed to fetch banners');
            setBanners([]);
            setFilteredBanners([]);
        } finally {
            setLoading(false);
        }
    };

    const calculateLocalStats = (bannersData = banners) => {
        const stats = {
            total: bannersData.length,
            active: bannersData.filter(b => b.is_active === 1).length,
            inactive: bannersData.filter(b => b.is_active === 0).length
        };
        setBannerStats(stats);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            // Validation
            if (!formData.title || !formData.description) {
                setError('Title and description are required');
                return;
            }

            if (!imageFile) {
                setError('Banner image is required');
                return;
            }

            if (formData.position && (formData.position < 1 || formData.position > 100)) {
                setError('Position must be between 1 and 100');
                return;
            }

            // Create FormData for file upload
            const formDataObj = new FormData();
            formDataObj.append('title', formData.title);
            formDataObj.append('description', formData.description);
            formDataObj.append('link_url', formData.link_url || '');
            formDataObj.append('position', formData.position || '1');
            formDataObj.append('is_active', formData.is_active);

            // Add dates if provided
            if (formData.starts_at) {
                formDataObj.append('starts_at', formData.starts_at);
            }
            if (formData.ends_at) {
                formDataObj.append('ends_at', formData.ends_at);
            }

            formDataObj.append('image_file', imageFile);

            console.log('Creating banner with data:', Object.fromEntries(formDataObj));

            const response = await bannersAPI.create(formDataObj);
            console.log('Create API Response:', response);

            if (response.data) {
                setSuccessMessage('Banner created successfully!');
                setShowCreateModal(false);
                resetForm();
                await fetchBanners();
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError(response.data?.message || 'Failed to create banner');
            }

        } catch (err) {
            console.error('Error creating banner:', err);
            setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to create banner');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!selectedBanner?.id) {
            setError('Banner ID is missing');
            return;
        }

        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            // Validation
            if (!formData.title || !formData.description) {
                setError('Title and description are required');
                return;
            }

            if (formData.position && (formData.position < 1 || formData.position > 100)) {
                setError('Position must be between 1 and 100');
                return;
            }

            // Based on the Postman collection, update might use JSON instead of FormData
            // Let's try both approaches

            const updateData = {
                title: formData.title,
                description: formData.description,
                link_url: formData.link_url || '',
                position: formData.position || '1',
                is_active: parseInt(formData.is_active)
            };

            // Add dates if provided
            if (formData.starts_at) {
                updateData.starts_at = formData.starts_at;
            }
            if (formData.ends_at) {
                updateData.ends_at = formData.ends_at;
            }

            console.log('Updating banner with data:', updateData);
            console.log('Banner ID:', selectedBanner.id);

            // Try JSON update first (as shown in Postman)
            const response = await bannersAPI.update(selectedBanner.id, updateData);
            console.log('Update API Response:', response);

            if (response.data) {
                setSuccessMessage('Banner updated successfully!');
                setShowEditModal(false);
                resetForm();
                setSelectedBanner(null);
                setIsEditing(false);
                await fetchBanners();
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError(response.data?.message || 'Failed to update banner');
            }

        } catch (err) {
            console.error('Error updating banner:', err);
            let errorMsg = 'Failed to update banner';
            if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
            } else if (err.response?.data?.error) {
                errorMsg = err.response.data.error;
            } else if (err.message) {
                errorMsg = err.message;
            }
            setError(errorMsg);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedBanner?.id) {
            setError('Banner ID is missing');
            setShowDeleteModal(false);
            return;
        }

        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            console.log('Deleting banner with ID:', selectedBanner.id);

            const response = await bannersAPI.delete(selectedBanner.id);
            console.log('Delete API Response:', response);

            if (response.status === 200 || response.status === 204 || response.data) {
                setSuccessMessage('Banner deleted successfully!');

                // Remove from local state immediately
                setBanners(prev => prev.filter(banner => banner.id !== selectedBanner.id));
                setFilteredBanners(prev => prev.filter(banner => banner.id !== selectedBanner.id));

                // Refresh stats
                calculateLocalStats(banners.filter(b => b.id !== selectedBanner.id));

                setShowDeleteModal(false);
                setSelectedBanner(null);
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError(response.data?.message || 'Failed to delete banner');
            }

        } catch (err) {
            console.error('Error deleting banner:', err);
            setError(err.response?.data?.message || err.message || 'Failed to delete banner');
        } finally {
            setActionLoading(false);
            setShowDeleteModal(false);
        }
    };

    const handleStatusToggle = async (bannerId, currentActive) => {
        try {
            setActionLoading(true);

            console.log(`Toggling status for banner ${bannerId}, current active: ${currentActive}`);

            const updatedData = {
                is_active: currentActive === 1 ? 0 : 1
            };

            const response = await bannersAPI.update(bannerId, updatedData);
            console.log('Toggle Status API Response:', response);

            if (response && response.data) {
                // Update local state with the response data
                const updatedBanner = response.data.data || response.data;

                setBanners(prev => prev.map(banner =>
                    banner.id === bannerId ? { ...banner, ...updatedBanner } : banner
                ));
                setFilteredBanners(prev => prev.map(banner =>
                    banner.id === bannerId ? { ...banner, ...updatedBanner } : banner
                ));

                // Refresh stats
                calculateLocalStats();

                setSuccessMessage(`Banner ${currentActive === 1 ? 'deactivated' : 'activated'} successfully!`);
                setTimeout(() => setSuccessMessage(''), 3000);
            }

        } catch (err) {
            console.error('Error changing banner status:', err);
            setError(err.response?.data?.message || err.message || 'Failed to change banner status');
        } finally {
            setActionLoading(false);
        }
    };

    const openEditModal = (banner) => {
        console.log('Opening edit modal for banner:', banner);
        setSelectedBanner(banner);

        // Parse dates properly
        const startsAt = banner.starts_at ? formatDateForInput(banner.starts_at) : '';
        const endsAt = banner.ends_at ? formatDateForInput(banner.ends_at) : '';

        setFormData({
            title: banner.title || '',
            description: banner.description || '',
            link_url: banner.link_url || '',
            position: banner.position?.toString() || '1',
            is_active: banner.is_active?.toString() || '1',
            starts_at: startsAt,
            ends_at: endsAt
        });

        // Set image preview
        if (banner.image_path) {
            const imageUrl = getBannerImageUrl(banner);
            setCurrentImagePath(banner.image_path);
            setImagePreview(imageUrl);
        } else {
            setCurrentImagePath(null);
            setImagePreview(null);
        }
        setImageFile(null);
        setIsEditing(true);
        setShowEditModal(true);
        setError('');
    };

    const openDeleteModal = (banner) => {
        console.log('Opening delete modal for banner:', banner);
        setSelectedBanner(banner);
        setShowDeleteModal(true);
        setError('');
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            link_url: '',
            position: '',
            is_active: '1',
            starts_at: '',
            ends_at: ''
        });
        setImagePreview(null);
        setImageFile(null);
        setCurrentImagePath(null);
        setIsEditing(false);
        setSelectedBanner(null);
        setError('');
    };

    const handleModalClose = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        resetForm();
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.pages) {
            setPagination(prev => ({ ...prev, page }));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const getBannerStatus = (banner) => {
        if (banner.is_active === 0) return 'inactive';

        const now = new Date();
        const startDate = banner.starts_at ? parseDate(banner.starts_at) : null;
        const endDate = banner.ends_at ? parseDate(banner.ends_at) : null;

        if (startDate && now < startDate) return 'upcoming';
        if (endDate && now > endDate) return 'expired';

        return 'active';
    };

    const parseDate = (dateString) => {
        if (!dateString) return null;
        try {
            if (dateString.includes('T')) {
                return new Date(dateString);
            } else if (dateString.includes('-')) {
                if (dateString.length === 10) {
                    return new Date(dateString + 'T00:00:00');
                }
                return new Date(dateString);
            } else {
                return new Date(dateString);
            }
        } catch {
            console.error('Error parsing date:', dateString);
            return null;
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <Badge bg="success" className="px-3 py-2">Active</Badge>;
            case 'inactive':
                return <Badge bg="secondary" className="px-3 py-2">Inactive</Badge>;
            case 'upcoming':
                return <Badge bg="warning" text="dark" className="px-3 py-2">Upcoming</Badge>;
            case 'expired':
                return <Badge bg="danger" className="px-3 py-2">Expired</Badge>;
            default:
                return <Badge bg="secondary" className="px-3 py-2">Unknown</Badge>;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = parseDate(dateString);
            if (!date || isNaN(date.getTime())) return dateString;

            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
            const date = parseDate(dateString);
            if (!date || isNaN(date.getTime())) {
                console.warn('Invalid date string for input:', dateString);
                return '';
            }

            // Get local date components (to avoid timezone issues)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');

            console.log(`Formatted date: ${dateString} -> ${year}-${month}-${day}`);
            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Error formatting date for input:', dateString, error);
            return '';
        }
    };

    const getBannerImageUrl = (banner) => {
        if (!banner.image_url) return null;

        // Handle both absolute and relative paths
        if (banner.image_url.startsWith('http')) {
            return banner.image_url;
        } else if (banner.image_url.startsWith('/')) {
            return `${BASE_URL}/public/uploads/banners/${banner.image_url}`;
        } else {
            return `${BASE_URL}/public/uploads/banners/${banner.image_url}`;
        }
    };

    const renderPagination = () => {
        if (pagination.pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.pages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        items.push(
            <Pagination.Prev
                key="prev"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
            />
        );

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} active={1 === pagination.page} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < pagination.pages) {
            if (endPage < pagination.pages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.pages}
                    active={pagination.pages === pagination.page}
                    onClick={() => handlePageChange(pagination.pages)}
                >
                    {pagination.pages}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                disabled={pagination.page === pagination.pages}
                onClick={() => handlePageChange(pagination.page + 1)}
            />
        );

        return items;
    };

    // Calculate displayed banners based on pagination
    const displayedBanners = filteredBanners.slice(
        (pagination.page - 1) * pagination.limit,
        pagination.page * pagination.limit
    );

    if (loading && !actionLoading) return (
        <div className="text-center py-5">
            <Spinner animation="border" variant="primary" role="status">
                <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2 text-muted">Loading banners...</p>
        </div>
    );

    return (
        <div>
            <Row className="align-items-center mb-4">
                <Col>
                    <h1 className="mb-0">Banners Management</h1>
                    <p className="text-muted mb-0">Manage promotional banners and marketing content</p>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="primary"
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="d-flex align-items-center me-2"
                        disabled={actionLoading}
                    >
                        <FaPlus className="me-2" />
                        Create Banner
                    </Button>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    <strong>Error:</strong> {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
                    <strong>Success:</strong> {successMessage}
                </Alert>
            )}

            {/* Stats Cards */}
            <Row className="mb-4 g-3">
                <Col md={4}>
                    <Card className="border-0 shadow-sm bg-primary text-white">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{bannerStats.total}</div>
                            <small>Total Banners</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm bg-success text-white">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{bannerStats.active}</div>
                            <small>Active</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm bg-secondary text-white">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{bannerStats.inactive}</div>
                            <small>Inactive</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={6}>
                            <div className="position-relative">
                                <FaSearch className="position-absolute top-50 start-3 translate-middle-y text-muted" />
                                <Form.Control
                                    type="text"
                                    placeholder="Search banners by title or description..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5"
                                    disabled={actionLoading}
                                />
                            </div>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="small mb-1">Filter by Status</Form.Label>
                                <Form.Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    size="sm"
                                    disabled={actionLoading}
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="upcoming">Upcoming</option>
                                    <option value="expired">Expired</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2} className="text-md-end d-flex align-items-center justify-content-end">
                            {(searchTerm || statusFilter !== 'all') && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setStatusFilter('all');
                                    }}
                                    disabled={actionLoading}
                                >
                                    Clear Filters
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th width="100">Image</th>
                                <th>Title</th>
                                <th>Description</th>
                                <th>Link URL</th>
                                <th>Position</th>
                                <th>Validity Period</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedBanners.length > 0 ? (
                                displayedBanners.map(banner => {
                                    const status = getBannerStatus(banner);
                                    const imageUrl = getBannerImageUrl(banner);
                                    return (
                                        <tr key={banner.id}>
                                            <td>
                                                {imageUrl ? (
                                                    <div style={{ width: '80px', height: '60px', overflow: 'hidden', borderRadius: '4px' }}>
                                                        <Image
                                                            src={imageUrl}
                                                            alt={banner.title}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = 'https://via.placeholder.com/80x60?text=No+Image';
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="bg-secondary rounded d-flex align-items-center justify-content-center"
                                                        style={{ width: '80px', height: '60px' }}
                                                    >
                                                        <FaImage className="text-white" size={20} />
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div>
                                                    <div className="fw-semibold">{banner.title}</div>
                                                    <small className="text-muted">
                                                        ID: {banner.id?.substring(0, 12)}...
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    <div className="text-truncate" style={{ maxWidth: '200px' }}>
                                                        {banner.description}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="text-truncate" style={{ maxWidth: '150px' }}>
                                                    {banner.link_url ? (
                                                        <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                                            {banner.link_url}
                                                        </a>
                                                    ) : (
                                                        <span className="text-muted">No link</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <Badge bg="info" className="px-3 py-2">
                                                    {banner.position || '1'}
                                                </Badge>
                                            </td>
                                            <td>
                                                <div className="small">
                                                    <div>
                                                        <FaCalendarAlt className="me-1" />
                                                        <strong>Start:</strong> {formatDate(banner.starts_at)}
                                                    </div>
                                                    <div>
                                                        <FaCalendarAlt className="me-1" />
                                                        <strong>End:</strong> {formatDate(banner.ends_at)}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    {getStatusBadge(status)}
                                                    <Button
                                                        variant={banner.is_active === 1 ? "outline-success" : "outline-secondary"}
                                                        size="sm"
                                                        className="p-1 d-flex align-items-center"
                                                        onClick={() => handleStatusToggle(banner.id, banner.is_active)}
                                                        title={banner.is_active === 1 ? 'Deactivate banner' : 'Activate banner'}
                                                        disabled={actionLoading}
                                                    >
                                                        {banner.is_active === 1 ? (
                                                            <>
                                                                <FaToggleOn className="me-1" size={20} />
                                                                <span className="small">Active</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaToggleOff className="me-1" size={20} />
                                                                <span className="small">Inactive</span>
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="small">
                                                    <div>{formatDate(banner.created_at)}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="d-flex align-items-center"
                                                        onClick={() => openEditModal(banner)}
                                                        title="Edit Banner"
                                                        disabled={actionLoading}
                                                    >
                                                        <FaEdit className="me-1" />
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        className="d-flex align-items-center"
                                                        onClick={() => openDeleteModal(banner)}
                                                        title="Delete Banner"
                                                        disabled={actionLoading}
                                                    >
                                                        <FaTrash className="me-1" />
                                                        Delete
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center py-5">
                                        <div className="text-muted">
                                            {searchTerm || statusFilter !== 'all' ? (
                                                <>
                                                    <FaSearch className="h3 mb-3" />
                                                    <p>No banners found matching your filters</p>
                                                </>
                                            ) : (
                                                <>
                                                    <FaImage className="h3 mb-3" />
                                                    <p>No banners found</p>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="py-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <small className="text-muted">
                                    Page {pagination.page} of {pagination.pages} •
                                    Showing {displayedBanners.length} of {pagination.total} banners
                                </small>
                            </div>
                            <Pagination className="mb-0">
                                {renderPagination()}
                            </Pagination>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Create Modal */}
            <FormModal
                show={showCreateModal}
                onHide={handleModalClose}
                title="Create New Banner"
                onSubmit={handleCreate}
                submitText={actionLoading ? "Creating..." : "Create Banner"}
                size="lg"
                loading={actionLoading}
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Title *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                                placeholder="Summer Sale 2024"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Position</Form.Label>
                            <Form.Control
                                type="number"
                                value={formData.position}
                                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                                placeholder="1"
                                min="1"
                                max="100"
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                Display order (lower numbers show first)
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Description *</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        required
                        placeholder="Up to 50% off on all golf equipment"
                        disabled={actionLoading}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Link URL</Form.Label>
                    <Form.Control
                        type="url"
                        value={formData.link_url}
                        onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                        placeholder="https://example.com/sale"
                        disabled={actionLoading}
                    />
                    <Form.Text className="text-muted">
                        URL to redirect when banner is clicked
                    </Form.Text>
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Start Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={formData.starts_at}
                                onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                Leave empty for immediate start
                            </Form.Text>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={formData.ends_at}
                                onChange={(e) => setFormData({ ...formData, ends_at: e.target.value })}
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                Leave empty for no expiry
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Banner Image *</Form.Label>
                    <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        required={!imagePreview}
                        disabled={actionLoading}
                    />
                    <Form.Text className="text-muted">
                        Recommended size: 1200x400px (JPG, PNG, or GIF)
                    </Form.Text>
                </Form.Group>

                {imagePreview && (
                    <div className="mb-3">
                        <Form.Label>Image Preview</Form.Label>
                        <div style={{ maxWidth: '400px', maxHeight: '200px', overflow: 'hidden', borderRadius: '4px' }}>
                            <Image
                                src={imagePreview}
                                alt="Preview"
                                fluid
                                style={{ width: '100%', height: 'auto' }}
                            />
                        </div>
                    </div>
                )}

                <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                        value={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                        disabled={actionLoading}
                    >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                    </Form.Select>
                </Form.Group>
            </FormModal>

            {/* Edit Modal */}
            <FormModal
                show={showEditModal}
                onHide={handleModalClose}
                title={`Edit Banner: ${selectedBanner?.title || ''}`}
                onSubmit={handleUpdate}
                submitText={actionLoading ? "Updating..." : "Update Banner"}
                size="lg"
                loading={actionLoading}
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Title *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Position</Form.Label>
                            <Form.Control
                                type="number"
                                value={formData.position}
                                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                                min="1"
                                max="100"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Description *</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        required
                        disabled={actionLoading}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Link URL</Form.Label>
                    <Form.Control
                        type="url"
                        value={formData.link_url}
                        onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                        disabled={actionLoading}
                    />
                </Form.Group>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Start Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={formData.starts_at}
                                onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={formData.ends_at}
                                onChange={(e) => setFormData({ ...formData, ends_at: e.target.value })}
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label>Banner Image</Form.Label>
                    <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={actionLoading}
                    />
                    <Form.Text className="text-muted">
                        {imageFile ? 'New image selected' : 'Leave empty to keep current image'}
                    </Form.Text>
                </Form.Group>

                {imagePreview && (
                    <div className="mb-3">
                        <Form.Label>Image Preview</Form.Label>
                        <div style={{ maxWidth: '400px', maxHeight: '200px', overflow: 'hidden', borderRadius: '4px' }}>
                            <Image
                                src={imagePreview}
                                alt="Preview"
                                fluid
                                style={{ width: '100%', height: 'auto' }}
                            />
                        </div>
                        {currentImagePath && !imageFile && (
                            <div className="mt-2 small text-muted">
                                Current image: {currentImagePath}
                            </div>
                        )}
                    </div>
                )}

                <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                        value={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                        disabled={actionLoading}
                    >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                    </Form.Select>
                </Form.Group>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => !actionLoading && setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Banner"
                message={
                    <div>
                        <p>Are you sure you want to delete banner <strong>"{selectedBanner?.title}"</strong>?</p>
                        <div className="alert alert-warning small">
                            <strong>Warning:</strong> This action cannot be undone. This banner will be removed from all displays.
                        </div>
                        <div className="small">
                            <strong>Banner Details:</strong>
                            <ul className="mb-0">
                                <li>Description: {selectedBanner?.description}</li>
                                <li>Position: {selectedBanner?.position || '1'}</li>
                                <li>Status: {selectedBanner && getStatusBadge(getBannerStatus(selectedBanner))}</li>
                            </ul>
                        </div>
                        {selectedBanner?.image_path && (
                            <div className="mt-2">
                                <strong>Preview:</strong>
                                <div style={{ width: '200px', height: '80px', overflow: 'hidden', borderRadius: '4px', marginTop: '8px' }}>
                                    <Image
                                        src={getBannerImageUrl(selectedBanner)}
                                        alt={selectedBanner.title}
                                        fluid
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'https://via.placeholder.com/200x80?text=Image+Removed';
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        <p className="text-muted small mt-2 mb-0">
                            Banner ID: <code>{selectedBanner?.id}</code>
                        </p>
                    </div>
                }
                confirmText={actionLoading ? "Deleting..." : "Delete"}
                confirmDisabled={actionLoading}
                variant="danger"
                loading={actionLoading}
            />
        </div>
    );
};
export default Banners;